import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { formatCalendarDate, formatInstant, formatInstantTime } from '../../lib/calendar-date';
import { WORK_LOCATIONS, WORK_LOCATION_LABELS, workLocationLabel } from '../../constants/work-locations';
import { toDatetimeLocal, fromDatetimeLocal } from '../../lib/datetime-local';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { fmtCurrency, fmtHours } from '../../lib/display-labels';
import { shiftHoursText } from '../../lib/shift-hours';
import { payGapNote } from '../../lib/pay-gap';
import {
  isFlaggedShift, longShiftOf, longShiftLabel, otherAnomalyFlags, LONG_SHIFT_NOTE_MAX,
  type LongShift, type LongShiftConfirmation,
} from '../../lib/long-shift';
import {
  readPeriodClosed, reopenPayPeriod, reopenTargetFor, ReopenPeriodButton, ReopenPayPeriodConfirm, type PeriodClosed,
} from './ReopenPayPeriod';
import {
  X,
  Clock,
  MapPin,
  FileText,
  AlertTriangle,
  Image,
  Loader2,
  Edit3,
  History,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import type { TimesheetAuditRow } from '../../types/timesheet-self-edit';
import type {
  AdminEditPayBranch, TimesheetAdminEditBody, TimesheetAdminEditResponse,
} from '../../types/timesheet-admin-edit';
import { shiftJobNumber } from '../../lib/shift-job-number';

/* ── Shared skins ──
 *
 * THIS FILE USED TO CARRY A HARDCODED DARK PALETTE, and so did the two halves
 * of the screen it renders inside. That meant the whole of /payroll/approvals
 * stayed navy while the app around it went grey-and-white, and the three files
 * had to keep their hex values in step by hand. Everything here is now a
 * `--fieldiq-*` surface/ink/edge token or a `--fi-signal-*` accent, which is
 * what makes the panel follow the theme switch with the rest of the app.
 */

/** A card inside the detail: the page's panel surface and edge. */
const CARD_CLASS = 'rounded-fi-panel border border-fieldiq-border bg-fieldiq-card';

/** Text inputs and selects, matching the app's other forms. */
const INPUT_CLASS =
  'w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 py-2 text-sm text-fieldiq-text focus:border-fi-amber focus:outline-none';

/* ── Types ── */
interface TimesheetDetail {
  id: string;
  user_id: string;
  worker_name: string;
  worker_initials?: string;
  entry_date: string;
  project?: string;
  status: 'submitted' | 'flagged' | 'approved' | 'rejected';
  clock_in_at: string;
  clock_out_at: string | null;
  raw_minutes: number;
  /**
   * Minutes between the ROUNDED punches (api#823). NULL on every shift closed
   * while rounding was off, which means "use raw_minutes". Read only through
   * shiftHoursText / minutesOfRecord.
   */
  payable_minutes?: number | string | null;
  regular_hours: number;
  overtime_hours: number;
  doubletime_hours?: number | null;
  standby_hours?: number;
  clock_in_lat?: number | null;
  clock_in_lng?: number | null;
  /** `text[]` on the server. Read only through lib/long-shift.ts. */
  anomaly_flags?: string[] | string | null;
  /** The API's long-shift verdict (api#1010). Read only through lib/long-shift.ts. */
  is_long_shift?: boolean;
  worker_note?: string;
  note?: string;
  photos?: string[];
  events?: TimesheetEvent[];
  /**
   * `pc.name` off a LEFT JOIN, so it is undefined whenever `pay_code_id` is
   * NULL — which is every row written since the clock-in screen stopped
   * sending one. Render it as an em dash, never as a shift kind.
   */
  pay_code_name?: string;
  /** 'shop' | 'field' | null (api 0333). NULL means nobody said — render '—'. */
  work_location?: string | null;
  job_name?: string;
  /** See shiftJobNumber. */
  job_number?: string | null;
  display_job_number?: string | null;
  /** `t.*` off GET /timesheets/:id. The period a Reopen from a correction's refusal targets. */
  pay_period_id?: string | null;
  /**
   * api #595: the worker moved this row AFTER handing it in and before anyone
   * signed for it. Derived from the audit trail on the server, never stored;
   * absent on an older API and then simply not shown.
   */
  edited_after_submission?: boolean;
  /**
   * How the shift was recorded. 'manual' is a missed shift the worker entered
   * by hand (api#1148): no clock punch backs it, and its reason is the note.
   */
  clock_source?: string | null;
  /** Why an approved shift has no pay entry (api#1148). Read only through lib/pay-gap. */
  pay_gap?: string | null;
  pay_gap_message?: string | null;
}

/**
 * Every key api #595 writes into an audit row's `changes`: the five
 * worker-editable fields plus the five figures a clock-out edit derives —
 * and the three api #716's office correction adds: `clock_in_at`, and the
 * shop pay entry's hours and amount when the correction re-priced it.
 * Exhaustive on purpose (CLAUDE.md: label maps, never binary). A key not
 * here is a field the API grew after this map — it renders as its raw
 * name, which is honest, rather than being dropped, which is not.
 */
const CHANGE_FIELD_LABELS: Record<string, string> = {
  clock_in_at: 'Clock in',
  clock_out_at: 'Clock out',
  per_diem_amount: 'Per diem',
  worker_note: 'Note',
  pay_code_id: 'Pay code',
  work_location: 'Location',
  raw_minutes: 'Raw minutes',
  payable_minutes: 'Rounded minutes',
  standby_minutes: 'Standby minutes',
  regular_hours: 'Regular hours',
  overtime_hours: 'OT hours',
  doubletime_hours: 'Double-time hours',
  shop_pay_hours: 'Shop pay hours',
  shop_pay_amount: 'Shop pay',
};

/** Timestamps read as clock times; money as money; everything else as itself; absent as a dash. */
const CHANGE_TIMESTAMP_FIELDS = new Set(['clock_in_at', 'clock_out_at']);
const CHANGE_MONEY_FIELDS = new Set(['shop_pay_amount']);
function changeValueLabel(field: string, v: unknown, timeZone: string | null): string {
  if (v === null || v === undefined || v === '') return '—';
  // On the tenant's clock (companies.time_zone), not the reader's device.
  if (CHANGE_TIMESTAMP_FIELDS.has(field)) return formatInstant(String(v), timeZone) ?? String(v);
  if (CHANGE_MONEY_FIELDS.has(field)) return fmtCurrency(v as number | string);
  return String(v);
}

const AUDIT_ACTION_LABELS: Record<string, string> = {
  created: 'Created',
  updated: 'Edited',
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected',
  locked: 'Locked',
  admin_edit: 'Corrected by the office',
};

interface TimesheetEvent {
  type: 'clock_in' | 'clock_out' | 'project_switch' | 'standby_start' | 'standby_end';
  time: string;
  label: string;
}

interface TimesheetDetailModalProps {
  timesheetId: string;
  /** Whether Approve and Reject are offered. Independent of the correction below. */
  context: 'approval' | 'readonly';
  onClose: () => void;
  /**
   * `confirmation` is present only when the approver confirmed a long shift
   * (api#992) — a flagged row asks before calling this at all.
   */
  onApprove?: (id: string, confirmation?: LongShiftConfirmation) => void;
  /** An approve is in flight: Confirm long shift is held so it cannot double-send. */
  approving?: boolean;
  /**
   * The row POST /approve just refused with 409 `long_shift_unconfirmed`. Opens
   * the same choice a flagged row gets — the API tests the shift's length, not
   * the flag, so a row closed before api#992 is refused with no flag on it.
   */
  longShiftRefused?: { id: string; longShift: LongShift } | null;
  onReject?: (id: string, reason: string, note: string) => void;
  /**
   * Offer the office correction (POST /timesheets/:id/admin-edit). The caller
   * decides with `canAdminEditTimesheet`. Not a third `context`: a correction
   * is offered on an approved or rejected row, where Approve and Reject are not.
   */
  canAdminEdit?: boolean;
  /** Offer Reopen when a correction is refused by an approved period. `canReopenPayPeriod`. */
  canReopenPeriod?: boolean;
  /**
   * The timesheet's own page (/payroll/approvals/:timesheetId). Set, this is
   * the element on the page-header band that holds the record's actions: Edit,
   * Reject and Approve are portalled into it, top right, and no close control
   * renders at all, because the band's back arrow is the way out. The Reject
   * and long-shift forms still open in the body's sticky bar, where their
   * reasons sit directly above the Confirm they gate, and the band's buttons
   * step aside while one is open, exactly as the footer's did.
   *
   * `null` while the band's slot has not mounted yet: nothing portals, and the
   * buttons appear on the next render. Unset (`undefined`), this is the
   * overlay's dialog shape with its X and footer.
   */
  bandActions?: HTMLElement | null;
  /**
   * The approval queue's page-scrolling layout (2026-10-08): the panel takes its
   * natural height, the PAGE scrolls, and the action bar sticks to the bottom of
   * the viewport inside the panel. Unset, the panel is a bounded box that
   * scrolls its own body — which is what the centred overlay below needs,
   * because it is capped at 90vh and has no page to scroll.
   */
  scrollsWithPage?: boolean;
}

/* ── Helpers ── */
function nameHash(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  const hues = [210, 260, 330, 30, 160, 45, 0, 190];
  return `hsl(${hues[Math.abs(h) % hues.length]}, 55%, 55%)`;
}

function initials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/** An instant, on the tenant's wall clock (companies.time_zone) — never the device's. */
function fmtTime(iso: string | null | undefined, timeZone: string | null): string {
  if (!iso) return '--';
  return formatInstantTime(iso, timeZone, { hour: 'numeric', minute: '2-digit', hour12: true }, 'en-US') ?? '—';
}

/**
 * `entry_date` is a CALENDAR DAY. `new Date('2026-07-14')` is UTC midnight,
 * i.e. 18:00 on the 13th in Alberta, so the date column read a day early while
 * fmtTime() beside it — reading the genuine instants `clock_in_at` /
 * `clock_out_at` — was right. The approver saw a shift dated the 13th with
 * clock times from the 14th.
 *
 * Only fmtDate converts. fmtTime above reads an instant, rendered on the
 * tenant's clock.
 */
function fmtDate(iso: string): string {
  return formatCalendarDate(iso, { month: 'short', day: 'numeric', year: 'numeric' }) ?? '--';
}

/* ── Sub-components ── */

function Avatar({ name, size = 48 }: { name: string; size?: number }) {
  return (
    <div
      className="flex items-center justify-center rounded-full font-bold text-white flex-shrink-0"
      style={{ width: size, height: size, backgroundColor: nameHash(name), fontSize: size * 0.38 }}
    >
      {initials(name)}
    </div>
  );
}

/**
 * The twin of the queue's map, and it has to be exhaustive for the same reason.
 *
 * The ternary chain this replaces ended `: 'Pending'`, so a `draft` row \u2014 now
 * reachable in this panel, because the Unsubmitted tab selects into it \u2014 was
 * badged "Pending" here while the row card three inches to the left said "Not
 * submitted". Two labels for one row, and the wrong one is the one that claims
 * the office has been handed hours nobody has handed them.
 */
const STATUS_PILL: Record<string, { label: string; fill: string }> = {
  draft: { label: 'Not submitted', fill: 'bg-fi-signal-slate' },
  submitted: { label: 'Pending', fill: 'bg-fi-signal-amber' },
  flagged: { label: 'Flagged', fill: 'bg-fi-signal-red' },
  approved: { label: 'Approved', fill: 'bg-fi-signal-green' },
  rejected: { label: 'Rejected', fill: 'bg-fi-signal-red' },
};

/**
 * The saturated signal badge, twin of the queue card's — dark ink on the fill,
 * never the pale 10% wash this used to draw. The `--fi-signal-*` hues have no
 * light twin, so one dark ink holds in both themes.
 *
 * An unrecognised status claims NO word and NO colour: slate, an em dash, and
 * the raw value on the accessible name where it can be read and looked up. It
 * must never fall back to a real status — on the panel someone signs against,
 * "Approved" is a claim that these hours are already counted.
 */
function StatusPill({ status }: { status: string }) {
  const known = Object.prototype.hasOwnProperty.call(STATUS_PILL, status);
  const { label, fill } = known
    ? STATUS_PILL[status]
    : { label: '—', fill: 'bg-fi-signal-slate' };
  return (
    <span
      aria-label={known ? undefined : `Unrecognised status: ${status}`}
      className={`inline-flex w-fit items-center rounded-fi-pill border border-black/20 px-2 py-0.5 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${fill}`}
    >
      {label}
    </span>
  );
}

/**
 * Same box as StatusPill, different word. Sits BESIDE the status, not in place
 * of it: "Pending" is still true of an edited row, and the approver needs both
 * facts before signing. Amber because it is the "look before you sign" colour
 * this screen already uses for Pending. Rendered by the queue's row and by this
 * detail's header so the list and the detail say it the same way.
 */
export function EditedPill() {
  return (
    <span
      /* A SATURATED BADGE, dark ink on the fill — the same idiom as the
         status pill beside it, and for the same measured reason. Amber ink on
         an amber wash reads 4.04 in light theme: the tint lifts the backdrop
         to rgb(238,230,209) and #b45309 cannot clear 4.5 against it. Dark ink
         on the solid signal hue is the shape that holds in both themes. */
      className="inline-flex w-fit items-center rounded-fi-pill border border-black/20 bg-fi-signal-amber px-2 py-0.5 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink"
      title="Changed by the worker after it was submitted"
    >
      Edited
    </span>
  );
}

/**
 * GET /audit/timesheet/:id, read with the detail rather than on expand.
 *
 * It was lazy while the card had one trigger, `edited_after_submission`, which
 * rides on the timesheet read. An office correction deliberately does not set
 * that flag — the pill means "the HAND moved this after handing it in" — and
 * nothing on the timesheet read says the office did. The `admin_edit` rows are
 * the only record of it, so whether the card exists at all is a question only
 * the trail can answer. Same query key the card reads, so it is one request.
 */
function useTimesheetHistory(timesheetId: string) {
  return useQuery<TimesheetAuditRow[]>({
    queryKey: ['timesheet-history', timesheetId],
    queryFn: () => api.timesheets.history(timesheetId).then((r) => {
      // Bare `{ data }` envelope on this route — no `success` key.
      const body: any = r.data;
      const list = body && typeof body === 'object' && 'data' in body ? body.data : body;
      return Array.isArray(list) ? list : [];
    }),
    enabled: !!timesheetId,
  });
}

/**
 * The trail behind the Edited pill and behind an office correction. The
 * `changes` from/to is written once by the API and shown here; nothing is
 * recomputed. Collapsed by default so the detail stays as short as it was.
 *
 * The title names which kinds of change are on the trail, because they mean
 * different things to an approver: the hand moving their own hours after
 * handing them in, and the office correcting them with a reason on record.
 * When the trail itself could not be read, it says so rather than naming
 * either — an unreadable trail is not a clean one.
 */
function EditHistoryCard({ timesheetId, workerEdited, officeEdited }: {
  timesheetId: string;
  workerEdited: boolean;
  officeEdited: boolean;
}) {
  const [open, setOpen] = useState(false);
  const timeZone = useTenantTimeZone();
  const { data: rows, isLoading, error } = useTimesheetHistory(timesheetId);
  // The edits are what the card promised; the status rows around them are
  // the context an approver needs to read "after it was submitted". An
  // office correction does not move the status, so it is named explicitly.
  const shown = (rows ?? []).filter(
    (r) => r.action === 'updated' || r.action === 'admin_edit' || r.previous_status !== r.new_status,
  );
  const title = [
    workerEdited ? 'Edited after submission' : null,
    officeEdited ? 'Corrected by the office' : null,
  ].filter(Boolean).join(' · ') || 'Change history';

  return (
    <div className="rounded-fi-panel border border-fi-amber/40 bg-fi-amber/10 p-4">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 text-left"
      >
        <span className="text-sm font-semibold flex items-center gap-2 text-fieldiq-accent-signal">
          <History size={14} />
          {title}
        </span>
        <span className="text-xs flex items-center gap-1 text-fieldiq-muted">
          {open ? 'Hide changes' : 'Show what changed'}
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </button>
      {open && (
        <div className="mt-3 space-y-3" role="list" aria-label="Changes on the trail">
          {isLoading && (
            <div className="flex justify-center py-3">
              <Loader2 size={18} className="animate-spin text-fieldiq-accent-signal" />
            </div>
          )}
          {!isLoading && error && (
            <p className="text-sm text-fieldiq-text">Couldn't load the change history.</p>
          )}
          {!isLoading && !error && shown.length === 0 && (
            <p className="text-sm text-fieldiq-muted">No changes on the trail.</p>
          )}
          {!isLoading && !error && shown.map((row) => (
            <div
              key={row.id}
              role="listitem"
              aria-label={`${AUDIT_ACTION_LABELS[row.action] ?? row.action} ${formatInstant(row.created_at, timeZone) ?? ''}`.trim()}
              className="text-sm text-fieldiq-text"
            >
              <p className="flex items-center justify-between gap-2">
                <span className="font-medium">
                  {AUDIT_ACTION_LABELS[row.action] ?? row.action}
                  {row.performed_by_role ? <span className="text-fieldiq-muted"> · {row.performed_by_role}</span> : null}
                </span>
                <span className="text-xs text-fieldiq-muted">
                  {formatInstant(row.created_at, timeZone) ?? '—'}
                </span>
              </p>
              {row.changes && Object.keys(row.changes).length > 0 && (
                <ul className="mt-1 space-y-0.5 text-xs text-fieldiq-muted">
                  {Object.entries(row.changes).map(([field, delta]) => (
                    <li key={field}>
                      <span className="text-fieldiq-text">{CHANGE_FIELD_LABELS[field] ?? field}</span>
                      {': '}
                      {changeValueLabel(field, delta?.from, timeZone)}
                      {' → '}
                      <span className="text-fieldiq-text">{changeValueLabel(field, delta?.to, timeZone)}</span>
                    </li>
                  ))}
                </ul>
              )}
              {/* On an office correction the note IS the reason the API required. */}
              {row.note && (
                <p className="text-xs mt-1 text-fieldiq-muted">
                  {row.action === 'admin_edit' ? `Reason: ${row.note}` : row.note}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Whether a branch moved money. Exhaustive over `AdminEditPayBranch` so a
 * branch the API adds later is a type error here rather than a silent "no".
 * Only a re-price rewrote an entry; every other branch changed hours only,
 * and the API's own sentence says which kind of "only".
 */
const PAY_BRANCH_MOVED_MONEY: Record<AdminEditPayBranch, boolean> = {
  field_shift_no_pay: false,
  shop_not_approved_no_pay: false,
  shop_approved_no_entry: false,
  shop_entry_repriced: true,
  shop_entry_unchanged: false,
};

/**
 * The office's sentence for an admin-edit refusal: the API's, as written —
 * with ONE exception.
 *
 * A US hourly worker with no hourly rate (api#1097) refuses a correction to an
 * already-priced shift with 409 `shop_pay_unpriceable`, `details.gap`
 * `no_hourly_rate_configured`. The API's text opens "This approved shop shift
 * has a pay entry…" — which on a FIELD shift names the wrong kind of shift,
 * and buries the fix in the middle of a compound sentence. The office needs
 * one thing from it: go and set the rate. So that one gap gets a plain
 * sentence, keyed on the code AND the gap (never on matching the prose), and
 * every other refusal — including the two sibling hourly gaps, whose API
 * wording already names the pay profile — renders exactly as before.
 */
export const NO_HOURLY_RATE_EDIT_MESSAGE =
  "This worker is paid hourly but has no hourly rate, so the corrected hours can't be priced and nothing was changed. "
  + "Set the worker's hourly rate on their pay profile, then save the correction again.";

export function adminEditRefusalMessage(err: any): string {
  const body = err?.response?.data;
  if (body?.code === 'shop_pay_unpriceable' && body?.details?.gap === 'no_hourly_rate_configured') {
    return NO_HOURLY_RATE_EDIT_MESSAGE;
  }
  return body?.error || 'Failed to save the correction.';
}

/**
 * What a saved correction did, said once, above the details it changed.
 *
 * The office has to know when an edit moved money: a re-priced shop entry
 * rewrote the worker's earnings and the period's totals in the same
 * transaction, and nothing else on this screen shows a pay figure. So that
 * branch is its own headline, with the before and after the API returned.
 */
export function AdminEditResult({ result, onDismiss }: { result: TimesheetAdminEditResponse; onDismiss: () => void }) {
  const pay = result.pay_effect;
  const movedMoney = pay ? PAY_BRANCH_MOVED_MONEY[pay.branch] === true : false;
  return (
    <div
      role="status"
      aria-label="Correction result"
      className={`rounded-fi-panel p-4 text-sm text-fieldiq-text border ${
        movedMoney ? 'border-fi-amber/40 bg-fi-amber/10' : 'border-fi-green/40 bg-fi-green/10'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          {!result.changed || !pay ? (
            // `changed: false` is a rollback on the server: no write, no audit row.
            <p className="font-semibold">Nothing changed — the values matched the shift, so no correction was recorded.</p>
          ) : movedMoney ? (
            <>
              {/* A US hourly hand's FIELD shift re-prices too (api#1097), and
                  calling that "shop pay" names the wrong kind of shift. The
                  row's own work_location decides; a shop shift reads as before. */}
              <p className="font-semibold text-fieldiq-accent-signal">
                {result.data?.work_location === 'field' ? 'Correction saved — pay was re-priced' : 'Correction saved — shop pay was re-priced'}
              </p>
              {pay.from && pay.to && (
                <p>
                  Pay {fmtCurrency(pay.from.amount)} → <span className="font-semibold">{fmtCurrency(pay.to.amount)}</span>
                  {' · '}Hours {pay.from.hours ?? '—'} → <span className="font-semibold">{pay.to.hours}</span>
                </p>
              )}
              <p className="text-fieldiq-muted">{pay.message}</p>
            </>
          ) : (
            <>
              <p className="font-semibold">Correction saved</p>
              <p className="text-fieldiq-muted">{pay.message}</p>
            </>
          )}
        </div>
        <button type="button" onClick={onDismiss} aria-label="Dismiss" className="p-1 rounded hover:bg-fieldiq-dark/60">
          <X size={14} className="text-fieldiq-muted" />
        </button>
      </div>
    </div>
  );
}

/**
 * The office correction form. Clock in, clock out, shop/field, and a reason.
 *
 * ── WHAT IT DOES NOT CHECK ──────────────────────────────────────────────────
 * The API refuses a clock-in moved to another tenant-local day, a shop/field
 * switch on an approved shift, an admin correcting their own shift, a closed
 * period, and hours that can no longer be priced. Every one of those is shown
 * as the API wrote it and none is re-derived here: the tenant's day is in the
 * tenant's zone, not this browser's, and a copy of any of those rules would
 * drift from the one that decides.
 *
 * What it does check is the form's own shape: a reason, and at least one field
 * that actually moved. A field is sent only when its picker string changed —
 * the picker cannot show seconds, so re-sending an untouched 07:02:37 as 07:02
 * would record a 37-second correction nobody made and re-derive the hours.
 *
 * ── THE REASON ──────────────────────────────────────────────────────────────
 * Written to the audit row's `note`, and read by someone a year from now who
 * was not in the room. Save stays disabled until it has something in it.
 */
function AdminEditPanel({ ts, canReopenPeriod, onSaved, onCancel }: {
  ts: TimesheetDetail;
  canReopenPeriod: boolean;
  onSaved: (result: TimesheetAdminEditResponse) => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  // The pickers show, and a changed picker writes, the TENANT's wall clock.
  const timeZone = useTenantTimeZone();
  const [initial] = useState(() => ({
    clockIn: toDatetimeLocal(ts.clock_in_at, timeZone),
    clockOut: toDatetimeLocal(ts.clock_out_at, timeZone),
    location: ts.work_location ?? '',
  }));
  const [clockIn, setClockIn] = useState(initial.clockIn);
  const [clockOut, setClockOut] = useState(initial.clockOut);
  const [location, setLocation] = useState(initial.location);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [periodClosed, setPeriodClosed] = useState<PeriodClosed | null>(null);
  const [showReopenConfirm, setShowReopenConfirm] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [reopenNotice, setReopenNotice] = useState<string | null>(null);

  const clearRefusal = () => { setError(null); setPeriodClosed(null); };

  const buildBody = (): TimesheetAdminEditBody | string => {
    const body: TimesheetAdminEditBody = { reason: reason.trim() };
    if (clockIn !== initial.clockIn) {
      const iso = fromDatetimeLocal(clockIn, timeZone);
      if (!iso) return 'Clock in needs a full date and time.';
      body.clock_in_at = iso;
    }
    if (clockOut !== initial.clockOut) {
      const iso = fromDatetimeLocal(clockOut, timeZone);
      if (!iso) return 'Clock out needs a full date and time.';
      body.clock_out_at = iso;
    }
    if (location !== initial.location && (location === 'shop' || location === 'field')) {
      body.work_location = location;
    }
    return body;
  };

  const moved = clockIn !== initial.clockIn || clockOut !== initial.clockOut || location !== initial.location;
  const hasReason = reason.trim() !== '';

  const mutation = useMutation({
    mutationFn: (body: TimesheetAdminEditBody) =>
      api.timesheets.adminEdit(ts.id, body).then((r) => r.data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['timesheet', ts.id] });
      queryClient.invalidateQueries({ queryKey: ['timesheet-history', ts.id] });
      queryClient.invalidateQueries({ queryKey: ['timesheets'] });
      onSaved(data);
    },
    onError: (err: any) => {
      // Every refusal's message is the API's, as written. A closed period
      // additionally says whether reopening it is a way through.
      setReopenNotice(null);
      setPeriodClosed(readPeriodClosed(err));
      setError(adminEditRefusalMessage(err));
    },
  });

  // ONE gate, on the button. Repeating it inside save() would leave two copies
  // of the rule, and deleting either would then change nothing a test can see.
  const canSave = hasReason && moved && !mutation.isPending;
  // Why Save is greyed out, beside it. A disabled button with no reason given
  // reads as a broken form. Nothing changed comes first: a reason for no change
  // would still not save.
  const saveHint = mutation.isPending ? null : !moved ? 'Change a time to save' : !hasReason ? 'Add a reason to save' : null;
  const saveHintId = `admin-edit-save-hint-${ts.id}`;

  const save = () => {
    const body = buildBody();
    if (typeof body === 'string') { setPeriodClosed(null); setError(body); return; }
    clearRefusal();
    mutation.mutate(body);
  };

  // The period that refused — for a shop shift, possibly the later period its
  // pay was carried into, which is still approved while the shift's own is open.
  const reopenTarget = periodClosed ? reopenTargetFor(periodClosed, ts.pay_period_id) : null;

  const reopen = async () => {
    if (!reopenTarget) return;
    setReopening(true);
    const result = await reopenPayPeriod(reopenTarget);
    setReopening(false);
    setShowReopenConfirm(false);
    if (result.ok) {
      // Reopen sends every timesheet in the period back to draft, this one
      // included, so the detail behind this form is stale either way.
      clearRefusal();
      setReopenNotice(`${result.message}. Save the correction again.`);
      queryClient.invalidateQueries({ queryKey: ['timesheet', ts.id] });
      queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    } else {
      setPeriodClosed(null);
      setError(result.message);
    }
  };

  const labelCls = 'block text-xs mb-1 text-fieldiq-muted';

  return (
    <div
      role="form"
      id={`admin-edit-${ts.id}`}
      aria-label="Correct this timesheet"
      className="rounded-fi-panel border border-fi-amber/40 bg-fieldiq-card p-4"
    >
      <h3 className="text-sm font-semibold mb-1 text-fieldiq-text">
        Correct this timesheet
      </h3>
      <p className="text-xs mb-3 text-fieldiq-muted">
        Recorded on the audit trail with what changed, who changed it, and your reason.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        <div>
          <label htmlFor="admin-edit-clock-in" className={labelCls}>Clock in</label>
          <input
            id="admin-edit-clock-in"
            type="datetime-local"
            value={clockIn}
            onChange={(e) => { setClockIn(e.target.value); clearRefusal(); }}
            className={INPUT_CLASS}
          />
        </div>
        <div>
          <label htmlFor="admin-edit-clock-out" className={labelCls}>Clock out</label>
          <input
            id="admin-edit-clock-out"
            type="datetime-local"
            value={clockOut}
            onChange={(e) => { setClockOut(e.target.value); clearRefusal(); }}
            className={INPUT_CLASS}
          />
        </div>
        <div>
          <label htmlFor="admin-edit-location" className={labelCls}>Where</label>
          <select
            id="admin-edit-location"
            value={location}
            onChange={(e) => { setLocation(e.target.value); clearRefusal(); }}
            className={INPUT_CLASS}
          >
            {/* NULL is "nobody said". It can be kept, not chosen — there is no way to set it back. */}
            {initial.location === '' && <option value="" disabled>—</option>}
            {WORK_LOCATIONS.map((l) => (
              <option key={l} value={l}>{WORK_LOCATION_LABELS[l]}</option>
            ))}
          </select>
        </div>
      </div>
      <label htmlFor="admin-edit-reason" className={labelCls}>
        Reason (required)
      </label>
      <textarea
        id="admin-edit-reason"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={2}
        placeholder="What was wrong, and how you know"
        className={`${INPUT_CLASS} resize-none py-2.5 mb-3`}
      />

      {reopenNotice && (
        <p role="status" className="text-sm mb-3 text-fieldiq-text">{reopenNotice}</p>
      )}
      {error && (
        <div
          role="alert"
          className="rounded-fi-control p-3 mb-3 text-sm flex items-start justify-between gap-3 border border-fi-red/30 bg-fi-red/10 text-fieldiq-text"
        >
          <span>{error}</span>
          {periodClosed?.canReopen && canReopenPeriod && reopenTarget && (
            <div className="shrink-0">
              <ReopenPeriodButton onClick={() => setShowReopenConfirm(true)} disabled={reopening} />
            </div>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 justify-end">
        {saveHint && (
          <p id={saveHintId} className="mr-auto text-xs text-fieldiq-muted">{saveHint}</p>
        )}
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 rounded-fi-control text-sm font-medium text-fieldiq-muted hover:bg-fieldiq-dark/60"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={save}
          disabled={!canSave}
          aria-describedby={saveHint ? saveHintId : undefined}
          className="fi-action fi-action--compact fi-action--primary disabled:opacity-40"
        >
          {mutation.isPending && <Loader2 size={14} className="animate-spin" />}
          Save correction
        </button>
      </div>

      <ReopenPayPeriodConfirm
        isOpen={showReopenConfirm}
        periodLabel={reopenTarget && reopenTarget !== ts.pay_period_id
          ? `The pay period holding this ${fmtDate(ts.entry_date)} shift's shop pay`
          : `The pay period holding this ${fmtDate(ts.entry_date)} shift`}
        loading={reopening}
        onConfirm={reopen}
        onCancel={() => setShowReopenConfirm(false)}
      />
    </div>
  );
}

function PhotoLightbox({ src, onClose }: { src: string; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80" onClick={onClose}>
      <img src={src} alt="Job attachment, enlarged" className="max-w-[90vw] max-h-[85vh] rounded-fi-panel shadow-2xl" />
      {/* Constant white on a constant black scrim — this layer does not follow
          the theme, so its ink must not either. */}
      <button onClick={onClose} aria-label="Close photo" className="absolute top-4 right-4 p-2 rounded-full bg-black/50 text-white hover:bg-black/70">
        <X size={20} />
      </button>
    </div>
  );
}

/**
 * ONE RECORDED FIGURE ON THE PANEL SOMEONE SIGNS AGAINST.
 *
 * These read `Number(x ?? 0).toFixed(2)`, so an absent figure rendered a
 * confident **0.00h** — on the four numbers an approver is attesting to, and
 * beside a queue card that already rendered the same absence as an em dash.
 * Two answers to one question, and the reassuring one was the one under the
 * Approve button.
 *
 * `fmtHours` rejects null, undefined and '' by identity BEFORE coercing, which
 * is the part that matters: `Number(null)` and `Number('')` are both 0. A real
 * zero is still data and still prints 0.00h. The suffix stays off the em dash —
 * "—h" reads as a unit with a missing number rather than as no answer.
 */
function hoursFigure(v: number | string | null | undefined): string {
  const s = fmtHours(v);
  return s === '—' ? s : `${s}h`;
}


const REJECTION_REASONS = [
  "Hours don't match schedule",
  'Missing project',
  'Incorrect times entered',
  'Duplicate entry',
  'Unapproved overtime',
  'Other',
];

/** POST /timesheets/:id/reject refuses a longer note (api#981, REJECT_NOTE_MAX). */
const REJECT_NOTE_MAX = 500;

/*
  Where the action bar sticks. In a bounded box it is the box's bottom edge.
  On the page it is the viewport's — less the mobile bottom nav below md, which
  overlays the page and would otherwise sit on top of Approve.
*/
const STICKY_BAR_BOX = 'sticky bottom-0 z-10';
const STICKY_BAR_PAGE = 'sticky bottom-[var(--bottom-chrome)] md:bottom-0 z-10';
/**
 * A secondary action on the navy band: a white-at-25% edge and NO colour
 * class, so it inherits the band's near-white in both themes (CLAUDE.md, "The
 * band"). Reject is one too — a red ink on navy is a signal hue used as ink.
 */
const BAND_SECONDARY =
  'inline-flex h-10 items-center gap-1.5 rounded-fi-control border border-white/25 px-4 text-sm font-bold transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50';

/**
 * WHY CONFIRM IS DISABLED, SAID WHERE THE BUTTON IS.
 *
 * POST /timesheets/:id/reject answers 400 "reason is required" without one, so
 * Confirm stays disabled until a reason is picked. That rule was always there;
 * what was missing was any way to see it — the reasons scrolled out of view and
 * nothing said a choice was owed. Now the reasons are a radio group directly
 * above Confirm, the first one takes focus when the form opens, and one line
 * says what is required. The line is the button's description, so a screen
 * reader hears why it is disabled too.
 *
 * THE NOTE TO THE WORKER IS OPTIONAL, AND IT RIDES BESIDE THE REASON, NEVER IN
 * IT. #941 removed the field because POST /reject read `reason` only and the
 * note was discarded. api#981 stores it (`supervisor_note`, read back as
 * `rejection_note` on the worker's reads) and texts it to the worker with the
 * reason and a link to /field/clock-in?timesheet=<id>. The route 400s a note
 * over REJECT_NOTE_MAX after trimming, so the input caps it here first. Do not
 * fold the note into `reason`: that changes what `rejection_reason` holds for
 * every other reader of it. A blank note is sent as absent, never ''.
 */
function RejectForm({
  stickyClass,
  onCancel,
  onConfirm,
}: {
  stickyClass: string;
  onCancel: () => void;
  onConfirm: (reason: string, note: string) => void;
}) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const firstReason = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    firstReason.current?.focus();
  }, []);
  const hintId = 'reject-reason-hint';
  return (
    <div
      role="group"
      aria-labelledby="reject-form-title"
      className={`${stickyClass} border-t border-fi-red/40 bg-fieldiq-card px-6 py-4`}
    >
      <h3 id="reject-form-title" className="text-sm font-semibold text-fieldiq-text">
        Reject timesheet
      </h3>
      <fieldset className="mt-2">
        <legend className="sr-only">Reason</legend>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {REJECTION_REASONS.map((r, i) => (
            <label
              key={r}
              className={`flex cursor-pointer items-center gap-2 rounded-fi-control border px-3 py-2 text-sm text-fieldiq-text transition-colors ${
                reason === r
                  ? 'border-fi-red bg-fi-red/20'
                  : 'border-fieldiq-border bg-fieldiq-dark hover:border-fi-red/60'
              }`}
            >
              <input
                ref={i === 0 ? firstReason : undefined}
                type="radio"
                name="reject-reason"
                value={r}
                checked={reason === r}
                onChange={() => setReason(r)}
                className="accent-fi-red"
              />
              {r}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="mt-3">
        <div className="flex items-baseline justify-between gap-3">
          <label htmlFor="reject-note" className="text-xs font-medium text-fieldiq-text">
            Note to worker (optional)
          </label>
          <span id="reject-note-count" className="text-xs tabular-nums text-fieldiq-muted">
            {note.length}/{REJECT_NOTE_MAX}
          </span>
        </div>
        <textarea
          id="reject-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={REJECT_NOTE_MAX}
          rows={2}
          aria-describedby="reject-note-count"
          placeholder="Texted to them with the reason"
          className="mt-1 w-full resize-y rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 py-2 text-sm text-fieldiq-text placeholder:text-fieldiq-muted focus:border-fi-red focus:outline-none"
        />
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-end gap-3">
        <p id={hintId} className="mr-auto text-xs text-fieldiq-muted">
          {reason ? `Reason: ${reason}` : 'Pick a reason to reject — it is required.'}
        </p>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 rounded-fi-control text-sm font-medium text-fieldiq-muted hover:bg-fieldiq-dark/60"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => reason && onConfirm(reason, note.trim())}
          disabled={!reason}
          aria-describedby={hintId}
          className="px-3 py-1.5 rounded-fi-control text-sm font-bold disabled:opacity-40 bg-fi-signal-red text-fi-signal-ink"
        >
          Confirm rejection
        </button>
      </div>
    </div>
  );
}

/**
 * A SHIFT OVER 16 HOURS NEEDS A DECISION BEFORE IT IS APPROVED (api#992).
 *
 * POST /approve refuses it 409 `long_shift_unconfirmed` unless the clock-out has
 * been corrected below the ceiling, or the body sends `confirm_long_shift: true`
 * with a non-blank `long_shift_note`. So the two ways through are offered side
 * by side, in the action bar where Approve was, exactly as the reject form is:
 *
 *   Fix clock-out       — opens the existing office correction. Strict admin, so
 *                         a dispatcher approving on a two-party tenant is told
 *                         who can, rather than shown a button that 403s.
 *   Confirm long shift  — a required note, trimmed; Confirm is held until it is
 *                         non-blank, because a blank one is a 400 on arrival.
 *
 * A forgotten clock-out approved with one click feeds the safety rates in full
 * (#969), which is why this asks rather than warns.
 */
function LongShiftForm({
  longShift,
  stickyClass,
  canAdminEdit,
  approving,
  onFixClockOut,
  onCancel,
  onConfirm,
}: {
  longShift: LongShift;
  stickyClass: string;
  canAdminEdit: boolean;
  approving: boolean;
  onFixClockOut: () => void;
  onCancel: () => void;
  onConfirm: (note: string) => void;
}) {
  const [note, setNote] = useState('');
  const noteRef = React.useRef<HTMLTextAreaElement>(null);
  const hintId = 'long-shift-hint';
  const canConfirm = note.trim() !== '' && !approving;
  return (
    <div
      role="group"
      aria-labelledby="long-shift-form-title"
      className={`${stickyClass} border-t border-fi-red/40 bg-fieldiq-card px-6 py-4`}
    >
      <h3 id="long-shift-form-title" className="flex items-center gap-2 text-sm font-semibold text-fieldiq-text">
        <AlertTriangle size={16} aria-hidden="true" />
        {longShiftLabel(longShift)}
      </h3>
      <p id={hintId} className="mt-1 text-xs text-fieldiq-muted">
        Over the 16-hour ceiling — fix the clock-out, or confirm the length with a note.
      </p>
      <div className="mt-3 flex flex-col gap-3 md:flex-row md:items-start">
        <div className="md:w-48 md:flex-shrink-0">
          {canAdminEdit ? (
            <button
              type="button"
              onClick={onFixClockOut}
              className="w-full px-4 py-2 rounded-fi-control text-sm font-semibold flex items-center justify-center gap-2 border border-fieldiq-border bg-fieldiq-card text-fieldiq-text hover:bg-fieldiq-dark/60 transition-colors"
            >
              <Edit3 size={15} aria-hidden="true" />
              Fix clock-out
            </button>
          ) : (
            <p className="text-xs text-fieldiq-muted">Only an admin can correct the clock-out.</p>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-3">
            <label htmlFor="long-shift-note" className="text-xs font-medium text-fieldiq-text">
              Why the length is right (required)
            </label>
            <span id="long-shift-note-count" className="text-xs tabular-nums text-fieldiq-muted">
              {note.length}/{LONG_SHIFT_NOTE_MAX}
            </span>
          </div>
          <textarea
            ref={noteRef}
            id="long-shift-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={LONG_SHIFT_NOTE_MAX}
            rows={2}
            aria-describedby="long-shift-note-count"
            className="mt-1 w-full resize-y rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 py-2 text-sm text-fieldiq-text placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
          />
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 rounded-fi-control text-sm font-medium text-fieldiq-muted hover:bg-fieldiq-dark/60"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => canConfirm && onConfirm(note.trim())}
          disabled={!canConfirm}
          aria-describedby={hintId}
          className="fi-action fi-action--standard fi-action--primary disabled:opacity-40"
        >
          {approving && <Loader2 size={14} className="animate-spin" aria-hidden="true" />}
          Confirm long shift
        </button>
      </div>
    </div>
  );
}

/**
 * A shift the worker entered by hand, with no clock punch behind it (api#1148).
 * A slate pill, because it is a fact about the record and not a status: the
 * approver signs it the same way, knowing the times are the worker's word.
 *
 * ⚠️ `'manual'` IS ALSO THE COLUMN'S DEFAULT. `timesheets.clock_source`
 * defaults to 'manual', so any insert that does not name its source reads as
 * "entered by hand" here — the sentinel-that-is-also-a-legal-value shape.
 * Measured 2026-10-23 on production: 345 of 345 rows are 'app_tap', none
 * 'manual', so today the pill is only ever a real missed-shift entry. The fix
 * for a second writer belongs in the API (name the source, or a non-'manual'
 * default), not in a guess here. Flagged in the PR for the API side.
 */
export function ManualEntryPill() {
  return (
    <span
      data-testid="manual-entry-pill"
      className="inline-flex w-fit items-center rounded-fi-pill border border-black/20 px-2 py-0.5 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink bg-fi-signal-slate"
    >
      Entered by hand
    </span>
  );
}

/**
 * Why an approved shift has no pay entry — see lib/pay-gap for what null
 * means (nothing recorded, never "paid"). The warning is an amber wash with an
 * amber rail and icon; the words stay in the theme ink, because a signal hue is
 * a fill and not an ink. `job_pay_exempt` is information, so it is slate.
 */
export function PayGapCard({ gap, message }: { gap: unknown; message: unknown }) {
  const note = payGapNote(gap, message);
  if (!note) return null;
  const warn = note.tone === 'warning';
  return (
    <div
      role="note"
      aria-label={note.title}
      data-testid="pay-gap-card"
      data-tone={note.tone}
      className={`rounded-fi-panel p-4 flex items-start gap-3 border border-l-4 ${
        warn ? 'border-fi-amber/40 border-l-fi-amber bg-fi-amber/10' : 'border-fieldiq-border border-l-fi-steel bg-fieldiq-dark/40'
      }`}
    >
      <AlertTriangle size={20} className={`flex-shrink-0 mt-0.5 ${warn ? 'text-fieldiq-accent-signal' : 'text-fieldiq-muted'}`} aria-hidden="true" />
      <div>
        <h3 className="text-sm font-semibold mb-1 text-fieldiq-text">{note.title}</h3>
        <p className="text-sm text-fieldiq-text">{note.message}</p>
      </div>
    </div>
  );
}

/* ── Main Component ── */
export default function TimesheetDetailModal({
  timesheetId,
  context,
  onClose,
  onApprove,
  approving = false,
  longShiftRefused = null,
  onReject,
  canAdminEdit = false,
  canReopenPeriod = false,
  bandActions,
  scrollsWithPage = false,
}: TimesheetDetailModalProps) {
  const onPage = bandActions !== undefined;
  const timeZone = useTenantTimeZone();
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  /*
    Both keyed by the timesheet they belong to, not booleans. The queue keeps
    this component mounted while the selection changes, so a flag would carry
    an open form — or another shift's "shop pay was re-priced" — onto the next
    row the office clicks.
  */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editResult, setEditResult] = useState<{ id: string; result: TimesheetAdminEditResponse } | null>(null);
  /*
    Keyed by the timesheet, like the two above and for their reason: a boolean
    carried an open reject form — with another shift's reason already ticked —
    onto the next row the office clicked.
  */
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  /* The long-shift choice, keyed the same way and for the same reason. */
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  /*
    A 409 opens it. The screen sets a fresh object per refusal (and clears it
    when the next approve starts), so a second refusal of the same row reopens
    a form the approver had cancelled.
  */
  React.useEffect(() => {
    if (longShiftRefused) { setConfirmingId(longShiftRefused.id); setRejectingId(null); }
  }, [longShiftRefused]);

  const { data: ts, isLoading, error } = useQuery<TimesheetDetail>({
    queryKey: ['timesheet', timesheetId],
    queryFn: () =>
      api.timesheets.get(timesheetId).then((r) => r.data?.data ?? r.data),
    enabled: !!timesheetId,
  });
  const { data: historyRows, error: historyError } = useTimesheetHistory(timesheetId);

  const effectiveStatus = ts && isFlaggedShift(ts) ? 'flagged' : ts?.status;
  /* The flag's figure, else is_long_shift's — see longShiftOf. */
  const flaggedLongShift = longShiftOf(ts);
  /* The figure for the form: that, else the 409's (a length the read missed). */
  const formLongShift: LongShift | null = !ts
    ? null
    : flaggedLongShift
      ?? (longShiftRefused?.id === ts.id ? longShiftRefused.longShift : null);
  const workerEdited = ts?.edited_after_submission === true;
  const officeEdited = (historyRows ?? []).some((r) => r.action === 'admin_edit');
  const noteText = ts?.worker_note || ts?.note;

  // Build timeline from events or fall back to clock_in/clock_out
  const timelineEvents: TimesheetEvent[] = ts?.events && ts.events.length > 0
    ? ts.events
    : ts
      ? [
          { type: 'clock_in' as const, time: ts.clock_in_at, label: 'Clock in' },
          ...(ts.clock_out_at
            ? [{ type: 'clock_out' as const, time: ts.clock_out_at, label: 'Clock out' }]
            : []),
        ]
      : [];

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-fieldiq-muted" />
      </div>
    );
  }

  if (error || !ts) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-fieldiq-muted">
          {error ? 'Failed to load timesheet' : 'Timesheet not found'}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className={scrollsWithPage ? 'flex-1 flex flex-col' : 'flex-1 flex flex-col overflow-hidden'}>
        {/* On its own page the band's back arrow is the way out; here, the X. */}
        {!onPage && (
          <div className="flex items-center justify-end px-4 pt-4">
            <button
              onClick={onClose}
              aria-label="Close"
              className="p-1.5 rounded-fi-control text-fieldiq-muted hover:bg-fieldiq-dark/60 transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        )}

        {/* Scrollable content */}
        <div className={`flex-1 px-6 pb-6 space-y-5${onPage ? ' pt-6' : ''}${scrollsWithPage ? '' : ' overflow-y-auto'}`}>
          {editResult?.id === ts.id && (
            <AdminEditResult result={editResult.result} onDismiss={() => setEditResult(null)} />
          )}

          {canAdminEdit && editingId === ts.id && (
            <AdminEditPanel
              key={ts.id}
              ts={ts}
              canReopenPeriod={canReopenPeriod}
              onSaved={(result) => { setEditingId(null); setEditResult({ id: ts.id, result }); }}
              onCancel={() => setEditingId(null)}
            />
          )}

          {/* Header */}
          <div className="flex items-center gap-4">
            <Avatar name={ts.worker_name} size={48} />
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-fieldiq-text">
                  {ts.worker_name}
                </h2>
                {ts.edited_after_submission === true && <EditedPill />}
                {ts.clock_source === 'manual' && <ManualEntryPill />}
                <StatusPill status={effectiveStatus ?? ts.status} />
              </div>
              <p className="text-sm text-fieldiq-muted">
                {fmtDate(ts.entry_date)}
              </p>
            </div>
          </div>

          {/* Why no pay was filed for this approved shift (api#1148). Nothing when nothing was recorded. */}
          <PayGapCard gap={ts.pay_gap} message={ts.pay_gap_message} />

          {/* Metrics row — every figure absent-safe. @see hoursFigure */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Total hours', value: shiftHoursText(ts.payable_minutes, ts.raw_minutes), tone: 'text-fieldiq-text' },
              { label: 'Regular', value: hoursFigure(ts.regular_hours), tone: 'text-fieldiq-text' },
              /*
                Amber only when there IS overtime. It was amber unconditionally,
                so a 0.00h read as a warning beside a neutral 0.00h Double-time.
                An absent value is not overtime either: it stays neutral and
                renders the dash. The ink is the theme-following accent token,
                never a signal hue (those are fills for dark ink).
              */
              {
                label: 'OT',
                value: hoursFigure(ts.overtime_hours),
                tone: Number(ts.overtime_hours) > 0 ? 'text-fieldiq-accent-signal' : 'text-fieldiq-text',
              },
              // Was 'Est. pay' off ts.gross_pay. Clock-out no longer computes a
              // pay figure — hours are what this record knows — and the
              // `?? 0` here would have rendered the absence as a confident
              // "$0.00" rather than a gap. Double-time is the fourth hours
              // bucket and is what the row actually carries.
              { label: 'Double-time', value: hoursFigure(ts.doubletime_hours), tone: 'text-fieldiq-text' },
            ].map((m) => (
              <div key={m.label} role="group" aria-label={m.label} className={`${CARD_CLASS} p-3`}>
                <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] mb-1 text-fieldiq-muted">
                  {m.label}
                </p>
                <p className={`text-lg font-bold tabular-nums ${m.tone}`}>
                  {m.value}
                </p>
              </div>
            ))}
          </div>

          {/* Shift details card */}
          <div className={`${CARD_CLASS} p-4`}>
            <h3 className="text-sm font-semibold mb-3 text-fieldiq-text">
              Shift details
            </h3>
            <div className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm">
              <div role="group" aria-label="Clock in">
                <p className="text-fieldiq-muted">Clock in</p>
                <p className="font-medium text-fieldiq-text">
                  {fmtTime(ts.clock_in_at, timeZone)}
                </p>
              </div>
              <div role="group" aria-label="Clock out">
                <p className="text-fieldiq-muted">Clock out</p>
                <p className="font-medium text-fieldiq-text">
                  {fmtTime(ts.clock_out_at, timeZone)}
                </p>
              </div>
              <div role="group" aria-label="Where">
                <p className="text-fieldiq-muted">Where</p>
                <p className="font-medium text-fieldiq-text">
                  {/*
                    Em dash for NULL, never a location — see workLocationLabel.
                    Note this sits ABOVE "Shift type" on purpose: since api 0333
                    the pay code is optional and absent on every tenant but the
                    Smith demo, so for a new row THIS field carries the answer
                    and "Shift type" is an em dash.
                  */}
                  {workLocationLabel(ts.work_location)}
                </p>
              </div>
              <div role="group" aria-label="Shift type">
                <p className="text-fieldiq-muted">Shift type</p>
                <p className="font-medium capitalize text-fieldiq-text">
                  {/*
                    Em dash, not 'Regular'. This read
                    `pay_code_name ?? shift_type ?? 'Regular'`, and both
                    fallbacks were wrong in a different way.

                    'Regular' is a manufactured answer to "what kind of shift
                    was this". The join is a LEFT JOIN precisely so a row with
                    no pay code survives, and since the clock-in screen stopped
                    sending `pay_code_id` that is every new row on every tenant
                    — so the default was about to become the usual reading.
                    A shift kind nobody recorded is not "Regular"; it is absent.

                    `shift_type` was never populated: no API read path selects
                    that column on a timesheet, so the middle branch could not
                    fire. It is off the shape above too.

                    The field itself stays. Smith's two pre-0333 draft rows
                    genuinely carry "Day Rate", and `PUT /timesheets/:id` can
                    still set one, so this is real historical record where it
                    exists. Compare `work_location` above, which is the field
                    that now carries the answer for new rows.
                  */}
                  {ts.pay_code_name ?? '—'}
                </p>
              </div>
              <div role="group" aria-label="Project / Job">
                <p className="text-fieldiq-muted">Project / Job</p>
                <p className="font-medium text-fieldiq-text">
                  {/* The job's number first (EWJ-0001, or a casing job's J-4329),
                      then its name. Either alone stands; neither is '—'. */}
                  {[shiftJobNumber(ts), ts.job_name ?? ts.project].filter(Boolean).join(' · ') || '—'}
                </p>
              </div>
              <div role="group" aria-label="Standby hours">
                <p className="text-fieldiq-muted">Standby hours</p>
                {/* The fifth figure of the same family: `?? 0` rendered an
                    absent standby as a confident 0.00h. @see hoursFigure */}
                <p className="font-medium tabular-nums text-fieldiq-text">
                  {hoursFigure(ts.standby_hours)}
                </p>
              </div>
              <div role="group" aria-label="Location verified">
                <p className="text-fieldiq-muted">Location verified</p>
                <p
                  className={`font-medium flex items-center gap-1 ${
                    ts.clock_in_lat != null ? 'text-fieldiq-text' : 'text-fieldiq-text'
                  }`}
                >
                  <MapPin size={13} />
                  {ts.clock_in_lat != null ? 'Yes' : 'No'}
                </p>
              </div>
            </div>
          </div>

          {/* Shift timeline */}
          {timelineEvents.length > 0 && (
            <div className={`${CARD_CLASS} p-4`}>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2 text-fieldiq-text">
                <Clock size={14} />
                Shift timeline
              </h3>
              <div className="relative pl-5">
                <div className="absolute left-[7px] top-1 bottom-1 w-px bg-fieldiq-border" />
                <div className="space-y-4">
                  {timelineEvents.map((ev, i) => (
                    <div key={i} className="relative flex items-start gap-3">
                      <div
                        className={`absolute -left-5 top-1.5 w-3 h-3 rounded-full border-2 border-fieldiq-card ${
                          ev.type === 'clock_in' || ev.type === 'clock_out'
                            ? 'bg-fi-signal-green'
                            : 'bg-fi-signal-amber'
                        }`}
                      />
                      <div>
                        <p className="text-sm font-medium text-fieldiq-text">
                          {ev.label}
                        </p>
                        <p className="text-xs text-fieldiq-muted">
                          {fmtTime(ev.time, timeZone)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Job notes */}
          {(noteText || (ts.photos && ts.photos.length > 0)) && (
            <div className={`${CARD_CLASS} p-4`}>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2 text-fieldiq-text">
                <FileText size={14} />
                Job notes
              </h3>
              {noteText && (
                <p className="text-sm mb-3 text-fieldiq-muted">
                  {noteText}
                </p>
              )}
              {ts.photos && ts.photos.length > 0 && (
                <>
                  <p className="text-xs font-medium mb-2 flex items-center gap-1.5 text-fieldiq-muted">
                    <Image size={12} />
                    Photos ({ts.photos.length})
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    {ts.photos.map((src, i) => (
                      <button
                        key={i}
                        onClick={() => setLightboxSrc(src)}
                        className="w-16 h-16 rounded-fi-control overflow-hidden border border-fieldiq-border bg-fieldiq-dark text-fieldiq-muted hover:opacity-80 transition-opacity flex items-center justify-center"
                      >
                        <img
                          src={src}
                          alt={`Job attachment ${i + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            /* The placeholder is written as markup rather than
                               rendered, so it cannot carry a class the scanner
                               would have to see. `currentColor` inherits the
                               muted ink from the button, which keeps it on the
                               theme without naming a colour here. */
                            (e.target as HTMLImageElement).style.display = 'none';
                            (e.target as HTMLImageElement).parentElement!.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;opacity:0.6"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg></div>`;
                          }}
                        />
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/*
            The trail card. The worker's edit is flagged on the timesheet read;
            the office's is only on the trail, so an admin_edit row raises the
            card too. A trail that failed to load raises it as well — the card
            then says it could not be read, which is true, where no card would
            claim a clean history nobody checked.
          */}
          {(workerEdited || officeEdited || !!historyError) && (
            <EditHistoryCard timesheetId={ts.id} workerEdited={workerEdited} officeEdited={officeEdited} />
          )}

          {/* Long-shift card (api#992). The figure is the flag's, or raw_minutes'. */}
          {flaggedLongShift && (
            <div
              data-testid="long-shift-card"
              className="rounded-fi-panel p-4 flex items-start gap-3 border border-fi-red/40 bg-fi-red/10"
            >
              <AlertTriangle size={20} className="flex-shrink-0 mt-0.5 text-fieldiq-text" aria-hidden="true" />
              <div>
                <h3 className="text-sm font-semibold mb-1 text-fieldiq-text">
                  {longShiftLabel(flaggedLongShift)}
                </h3>
                <p className="text-sm text-fieldiq-text">
                  Approving it needs a corrected clock-out or a note confirming the length.
                </p>
              </div>
            </div>
          )}

          {/* Any other flag, one line each, as the API wrote it. */}
          {otherAnomalyFlags(ts.anomaly_flags).length > 0 && (
            <div className="rounded-fi-panel p-4 flex items-start gap-3 border border-fi-red/40 bg-fi-red/10">
              <AlertTriangle size={20} className="flex-shrink-0 mt-0.5 text-fieldiq-text" aria-hidden="true" />
              <div>
                <h3 className="text-sm font-semibold mb-1 text-fieldiq-text">
                  Agent flag
                </h3>
                {otherAnomalyFlags(ts.anomaly_flags).map((flag) => (
                  <p key={flag} className="text-sm text-fieldiq-text">{flag}</p>
                ))}
              </div>
            </div>
          )}
        </div>

        {/*
          THE REJECT FORM LIVES IN THE ACTION BAR, NOT AT THE FOOT OF THE BODY.
          It used to render after every card in the panel, so opening it put the
          reasons below the fold while the bar holding Confirm stayed on screen
          — a disabled button whose reason for being disabled was out of sight.
          In the bar, the reason sits directly above Confirm and is sticky with
          it, so the two are always seen together.
        */}
        {confirmingId === ts.id && formLongShift && onApprove && context === 'approval' ? (
          <LongShiftForm
            key={ts.id}
            longShift={formLongShift}
            stickyClass={scrollsWithPage ? STICKY_BAR_PAGE : STICKY_BAR_BOX}
            canAdminEdit={canAdminEdit}
            approving={approving}
            onFixClockOut={() => {
              setConfirmingId(null);
              setEditingId(ts.id);
              setEditResult(null);
              // The correction opens at the top of the body; bring it into view.
              requestAnimationFrame(() =>
                document.getElementById(`admin-edit-${ts.id}`)?.scrollIntoView?.({ block: 'start', behavior: 'smooth' }),
              );
            }}
            onCancel={() => setConfirmingId(null)}
            onConfirm={(note) => onApprove(ts.id, { confirm_long_shift: true, long_shift_note: note })}
          />
        ) : rejectingId === ts.id && onReject ? (
          <RejectForm
            stickyClass={scrollsWithPage ? STICKY_BAR_PAGE : STICKY_BAR_BOX}
            onCancel={() => setRejectingId(null)}
            onConfirm={(reason, note) => {
              onReject(ts.id, reason, note);
              setRejectingId(null);
            }}
          />
        ) : onPage ? (
          /* On the record's page the actions are on the band, top right. */
          bandActions ? createPortal(
            <>
              {canAdminEdit && editingId !== ts.id && (
                <button
                  type="button"
                  onClick={() => { setEditingId(ts.id); setEditResult(null); }}
                  className={BAND_SECONDARY}
                >
                  <Edit3 size={15} aria-hidden="true" />
                  Edit
                </button>
              )}
              {context === 'approval' && (
                <>
                  <button type="button" onClick={() => setRejectingId(ts.id)} className={BAND_SECONDARY}>
                    Reject
                  </button>
                  {/* A long shift (flag or length) asks first; anything else sends. */}
                  <button
                    type="button"
                    onClick={() => {
                      if (flaggedLongShift) { setConfirmingId(ts.id); setRejectingId(null); }
                      else onApprove?.(ts.id);
                    }}
                    className="fi-action fi-action--standard fi-action--primary"
                  >
                    Approve
                  </button>
                </>
              )}
            </>,
            bandActions,
          ) : null
        ) : (
        <div className={`${scrollsWithPage ? STICKY_BAR_PAGE : STICKY_BAR_BOX} flex items-center justify-end gap-3 px-6 py-4 border-t border-fieldiq-border bg-fieldiq-card`}>
          {context === 'readonly' && (
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-fi-control text-sm font-medium text-fieldiq-muted hover:bg-fieldiq-dark/60 transition-colors"
            >
              Close
            </button>
          )}

          {canAdminEdit && editingId !== ts.id && (
            <button
              type="button"
              onClick={() => { setEditingId(ts.id); setEditResult(null); }}
              className="px-4 py-2 rounded-fi-control text-sm font-medium flex items-center gap-2 text-fieldiq-muted hover:bg-fieldiq-dark/60 transition-colors"
            >
              <Edit3 size={15} />
              Edit
            </button>
          )}

          {context === 'approval' && (
            <>
              <button
                onClick={() => setRejectingId(ts.id)}
                className="px-4 py-2 rounded-fi-control text-sm font-semibold transition-colors border border-fi-red/40 bg-fi-red/10 text-fieldiq-text hover:bg-fi-red/20"
              >
                Reject
              </button>
              {/* The one filled button in the footer. Approving is the decision
                  this panel exists for, and it is the amber primary everywhere
                  else in the app — the green outline it used to carry read as
                  the weaker of the two controls beside a filled Reject. */}
              {/* A long shift (flag or length) asks first; anything else sends.
                  Anything the API still refuses comes back through the 409. */}
              <button
                onClick={() => {
                  if (flaggedLongShift) { setConfirmingId(ts.id); setRejectingId(null); }
                  else onApprove?.(ts.id);
                }}
                className="fi-action fi-action--standard fi-action--primary"
              >
                Approve
              </button>
            </>
          )}
        </div>
        )}
      </div>

      {/* Photo lightbox */}
      {lightboxSrc && <PhotoLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
    </>
  );
}

/* ── Overlay variant (for use outside split layouts) ── */
export function TimesheetDetailOverlay(props: TimesheetDetailModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={props.onClose}>
      <div
        className="rounded-fi-panel shadow-xl w-full flex flex-col overflow-hidden border border-fieldiq-border bg-fieldiq-card"
        style={{ maxWidth: 720, maxHeight: '90vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <TimesheetDetailModal {...props} />
      </div>
    </div>
  );
}
